import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { LoggerService } from '@shared/modules/logger/service/logger.service';
import { WindowRef } from '@shared/services';
import { environment } from '@src/environments';
import { ClearStateAndStorage, SetAuthenticationStatus } from '@store/actions';
import * as fromRootStore from '@store/index';
import { selectVgClientPoid } from '@store/selectors/client-accounts/client-accounts.selectors';
import { Observable, of } from 'rxjs';
import { catchError, map, take, tap, withLatestFrom } from 'rxjs/operators';

import { getConfigClientFromRTE } from '../../config/config-client';
import { overviewRedirect } from '../init-state/init-state.guard';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private http: HttpClient,
    private meta: Meta,
    private windowRef: WindowRef,
    private store: Store<fromRootStore.State>,
    private logger: LoggerService,
  ) {}

  canActivate(
    _: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.http
      .post<{
        poid: string;
        sPoid: string;
      }>(`secure/auth`, {}, { withCredentials: true })
      .pipe(
        withLatestFrom(this.store.select(selectVgClientPoid)),
        map(([data, poid]) => {
          if (!!poid && poid !== data.poid) {
            this.logger.warn(
              {
                message: 'AuthGuard: poid mismatch — clearing state and redirecting to overview',
                eventId: 'auth-guard-poid-mismatch',
                source: 'AuthGuard',
                data: { storedPoid: poid, responsePoid: data.poid, url: state.url },
              },
              1001,
            );
            this.store.dispatch(ClearStateAndStorage());
            this.windowRef.nativeWindow.location.href = `${environment.applicationUrl}/${overviewRedirect(state.url)}`;
            return false;
          }
          this.meta.updateTag({ name: 'DCSext.poid', content: data.poid });
          // Medallia needz dis
          this.windowRef.nativeWindow.spoid = data.sPoid;
          return true;
        }),
        tap((isAuthenticated) => {
          // Dispatch authentication status to store once
          this.store.dispatch(SetAuthenticationStatus({ isAuthenticated }));
        }),
        take(1),
        catchError((error) => {
          // Redirect to logon if they are not authenticated, set target back to the current page once they logged in
          const targetOverride = `${environment.applicationUrl}${state.url}`;
          const isRKNLogin = getConfigClientFromRTE().isInternal;
          const redirectQueryParam = isRKNLogin ? 'logonTargetUrl' : 'TARGET_OVERRIDE';
          const href = `${isRKNLogin ? environment.rknLoginUrl : environment.logonUrl}?${redirectQueryParam}=${targetOverride}${!isRKNLogin ? '&authenticate=true' : ''}`;

          this.logger.error(
            {
              message: 'AuthGuard: auth endpoint error — redirecting to logon',
              eventId: 'auth-guard-endpoint-failure',
              source: 'AuthGuard',
              error: error.message,
              data: { url: state.url, isRKNLogin, status: error?.status },
            },
            1002,
          );

          // Dispatch failed authentication
          this.store.dispatch(SetAuthenticationStatus({ isAuthenticated: false }));
          this.windowRef.nativeWindow.location.href = href;
          return of(false);
        }),
      );
  }
}
